import React from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Menu } from '@wds/menu';
import { IconCaretDown } from '@wds/icons';
import { Link } from '@wds/link';

import PropTypes from 'prop-types';
import STRINGS from '../../../../constants';

import getComponentWithTheme from '../../../../../themeProvider';
import LanguageSelector from '../LanguageSelector';
import he from 'he';

const ApplicantNavigation = (props) => {
	const {
		userDetails, languages, assumedRole, siteInfo, language, setLanguage, pages, cdnUrl,
	} = props;
	
	const navBarItemProps = {
		mx: 4,
		display: 'flex',
		justifyContent: 'space-around',
		gap: 2,
		style: {
			color: '#efefef',
			cursor: 'pointer',
			fontSize: '1.5rem',
		},
	};

	const navBarMenuProps = {
		style: navBarItemProps.style,
		display: 'flex',
		justifyContent: 'space-around',
		alignItems: 'center',
		gap: 1,
	};

	const userMenuRoleChoices = [
		{
			label: STRINGS.navMenu.common.applicant,
			href: reverse('application-dashboard'),
			show: userDetails?.roles?.includes('applicant'),
			id: 'sma-applicant-role',
		},
		{
			label: STRINGS.navMenu.common.reviewer,
			href: reverse('reviewers-index'),
			show: userDetails?.roles?.includes('reviewer'),
			id: 'sma-reviewer-role',
		},
		{
			label: STRINGS.navMenu.common.recommender,
			href: reverse('recommenders-task-list'),
			show: userDetails?.roles?.includes('recommender'),
			id: 'sma-recommender-role',
		},
		{
			label: STRINGS.navMenu.common.administrator,
			href: reverse('admin-dashboard'),
			show: userDetails?.roles?.includes('admin'),
			id: 'sma-admin-role',
		},
	];

	const userMenuCommonItems = [
		{ label: STRINGS.navMenu.common.myAccount, href: reverse('account'), id: 'sma-my-account' },
		{ label: STRINGS.navMenu.common.signOut, href: reverse('signout') },
	];

	const helpMenuChoices = [
		{
			label: STRINGS.navMenu.common.contactAdmin,
			description: STRINGS.navMenu.common.contactAdminDesc,
			external: true,
			href: `mailto:${siteInfo.primaryAdminEmail}?Subject=${STRINGS.navMenu.common.contactAdminSubject} ${siteInfo.title}`,
			addOn: <i className='nc-icon-mini ui-1_email-83' />,
			id: 'sma-contact-admin-link',
		},
		{
			label: STRINGS.navMenu.common.faq,
			description: STRINGS.navMenu.common.faqDesc,
			external: true,
			href: 'https://help.surveymonkey.com/en/apply/faqs/#faq',
			addOn: <i className='nc-icon-mini arrows-e_share-26' />,
			id: 'sma-faq-link',
		},
		{
			label: STRINGS.navMenu.common.contactSupport,
			description: STRINGS.navMenu.common.contactSupportDesc,
			external: true,
			href: reverse('helpdesk'),
			addOn: <i className='nc-icon-mini arrows-e_share-26' />,
			id: 'sma-contact-support-link',
		},
	];
	
	const pageMenuItems = pages?.map((page) => ({
		label: page.name,
		href: page.url,
		external: true,
	}));

	const getMenuRoleItems = () => {
		const items = [];
		userMenuRoleChoices?.forEach((item) => {
			if (item.show) {
				items.push(
					<Menu.Item
						id={item.id}
						key={item.href}
						onClick={() => {
							window.location.href = item.href;
						}}
					>
						{item.label}
					</Menu.Item>,
				);
			}
		});
		if (items.length > 1) {
			return (
				<Menu.Group
					title={STRINGS.navMenu.common.switchRoleTo}
					aria-label={STRINGS.navMenu.common.switchRoleTo}
				>
					{items}
				</Menu.Group>
			);
		}
	};

	const userMenuItems = () => (
		<>
			{getMenuRoleItems()}
			{assumedRole && (
				<Menu.Group aria-label={STRINGS.labels.assumedRoleControls}>
					<Menu.Item
						id='sma-assumed-role-return'
						key='assumed-role-return'
						onClick={() => {
							window.location.href = assumedRole.return_url;
						}}
					>
						{STRINGS.returnToOriginalRole}
					</Menu.Item>
				</Menu.Group>
			)}
			<Menu.Group aria-label={STRINGS.navMenu.common.userMenu}>
				{userMenuCommonItems.map((item) => (
					<Menu.Item
						id={item.id}
						key={item.href}
						onClick={() => {
							window.location.href = item.href;
						}}
					>
						{item.label}
					</Menu.Item>
				))}
			</Menu.Group>
		</>
	);

	const navMenuItems = [
		{
			id: 'sma-nav-pages',
			title: 'Pages',
			icon: 'nc-icon-mini files_single-paragraph',
			menuItems: pageMenuItems,
			showTitle: true,
			show: pages?.length > 0,
		},
		{
			id: 'sma-nav-programs',
			title: 'Programs',
			icon: 'nc-icon-mini business_award-48',
			href: reverse('program-list'),
			showTitle: true,
			show: true,
		},
		{
			id: 'sma-nav-my-applications',
			title: 'My Applications',
			icon: 'nc-icon-mini ui-1_home-51',
			href: reverse('application-dashboard'),
			showTitle: true,
			show: true,
		},
		{
			id: 'sma-nav-help',
			title: 'Help',
			icon: 'nc-icon-mini ui-2_alert-circle-i',
			menuItems: helpMenuChoices,
			showTitle: false,
			show: true,
		},
	];

	return (
		getComponentWithTheme(
			<Box
				display='flex'
				justifyContent='space-between'
				alignItems='center'
				style={{
					padding: '0 1rem',
					height: '48px',
					backgroundColor: '#393939',
					width: '100%',
					position: 'fixed',
					zIndex: 1033,
				}}
			>
				<Box
					element='a'
					display='flex'
					justifyContent='flex-start'
					href={reverse('program-list')}
					alignItems='center'
					gap={4}
				>
					<img src={`${cdnUrl}img/smapply/monkey_head.svg`} height='36' alt='' />
					<Typography color='light' weight='medium' variant='body'>{he.decode(siteInfo.title)}</Typography>
				</Box>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
				>
					<Box {...navBarMenuProps} mr={5}>
						<LanguageSelector
							languages={languages}
							currentLanguage={language}
							setCurrentLanguage={setLanguage}
							type='menu'
						/>
					</Box>
					{
						navMenuItems.map((item) => {
							if (item.show) {
								if (item.menuItems) {
									return (
										<Box
											{...navBarMenuProps}
											key={`navItem-${item.id}`}
											role='button'
											aria-label={item.title}
										>
											<Menu
												id={item.id}
												stretched
												menuTrigger={(
													<div>
														<Box
															display='flex'
															justifyContent='space-around'
															alignItems='center'
															gap={0}
														>
															<Box {...navBarItemProps}>
																<Typography
																	component='span'
																	color='light'
																	weight='medium'
																	variant='body'
																>
																	<i className={item.icon} />
																</Typography>
																{item.showTitle && (
																	<Typography
																		component='span'
																		color='light'
																		weight='medium'
																		variant='body'
																	>
																		{item.title}
																	</Typography>
																)}
															</Box>
															<IconCaretDown color='light' size='sm' />
														</Box>
													</div>
												)}
												noRules
												noMaxHeight
												placement='bottom-left'
											>
												{item.menuItems && item.menuItems.map((subItem) => (
													<Menu.Item
														id={subItem.id}
														key={subItem.href}
														addOn={subItem.icon}
														onClick={() => {
															if (subItem.external) {
																window.open(subItem.href, '_blank');
															} else {
																window.location.href = subItem.href;
															}
														}}
													>
														<Box py={2}>
															<Box
																display='flex'
																justifyContent='space-between'
																alignItems='center'
																gap={2}
															>
																<Typography
																	component='span'
																	variant='body'
																	weight='medium'
																>
																	{subItem.label}
																</Typography>
																{subItem.addOn}
															</Box>
															{subItem.description && (
																<Typography component='span' variant='bodySm'>
																	{subItem.description}
																</Typography>
															)}
														</Box>
													</Menu.Item>
												))}
											</Menu>
										</Box>
									);
								}
								return (
									<Link
										key={item.href}
										href={item.href}
										underline={false}
										role='link'
										aria-label={item.title}
									>
										<Box {...navBarItemProps}>
											<Typography
												component='span'
												color='light'
												weight='medium'
												variant='body'
											>
												<i className={item.icon} />
											</Typography>
											{item.showTitle && (
												<Typography
													component='span'
													color='light'
													weight='medium'
													variant='body'
												>
													{item.title}
												</Typography>
											)}
										</Box>
									</Link>
								);
							}
							return null;
						})
					}
					<Box id='sma-user-menu' {...navBarMenuProps} ml={4}>
						<Menu
							id='sma-user-menu-dropdown'
							menuTrigger={(
								<div>
									<Box display='flex' justifyContent='space-around' alignItems='center' gap={2}>
										<Typography
											component='span'
											color='light'
											weight='medium'
											variant='body'
										>
											{userDetails.firstName}
											{' '}
											{userDetails.lastName}
										</Typography>
										<IconCaretDown color='light' size='sm' />
									</Box>
								</div>
							)}
							noRules
							noMaxHeight
							placement='bottom-center'
						>
							{userMenuItems()}
						</Menu>
					</Box>
				</Box>
			</Box>,
		)
	);
};

ApplicantNavigation.propTypes = {
	userDetails: PropTypes.object.isRequired,
	languages: PropTypes.object.isRequired,
	assumedRole: PropTypes.object,
	siteInfo: PropTypes.object.isRequired,
	pages: PropTypes.array,
	language: PropTypes.string.isRequired,
	setLanguage: PropTypes.func.isRequired,
	cdnUrl: PropTypes.string.isRequired,
};
ApplicantNavigation.defaultProps = {
	assumedRole: {},
	pages: [],
};

export default ApplicantNavigation;
